import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

const QuizTimer = ({ durationSeconds, onTimeExpire }) => {
  const [remainingSeconds, setRemainingSeconds] = useState(
    Math.max(durationSeconds ?? 0, 0),
  );

  const expiredRef = useRef(false);

  /**
   * Reset timer when duration changes
   */
  useEffect(() => {
    const safeDuration = Math.max(durationSeconds ?? 0, 0);

    setRemainingSeconds(safeDuration);

    expiredRef.current = false;
  }, [durationSeconds]);

  /**
   * Countdown timer
   */
  useEffect(() => {
    if (remainingSeconds <= 0) {
      if (!expiredRef.current) {
        expiredRef.current = true;

        onTimeExpire?.();
      }

      return undefined;
    }

    const timer = setInterval(() => {
      setRemainingSeconds((previous) => (previous > 0 ? previous - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [remainingSeconds, onTimeExpire]);

  const formatTime = (seconds) => {
    const safeSeconds = Math.max(seconds, 0);

    const minutes = Math.floor(safeSeconds / 60);

    const remaining = safeSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(remaining).padStart(
      2,
      "0",
    )}`;
  };

  const getTimerColor = () => {
    if (remainingSeconds <= 60) {
      return "text-red-600 bg-red-50";
    }

    if (remainingSeconds <= 300) {
      return "text-orange-600 bg-orange-50";
    }

    return "text-green-600 bg-green-50";
  };

  return (
    <div
      className={`
        flex
        items-center
        justify-center
        rounded-lg
        px-4
        py-2
        font-semibold
        ${getTimerColor()}
      `}
      aria-live="polite"
      aria-label={`Time remaining ${formatTime(remainingSeconds)}`}
    >
      <span className="mr-2">Time Left:</span>

      <span>{formatTime(remainingSeconds)}</span>
    </div>
  );
};

QuizTimer.propTypes = {
  durationSeconds: PropTypes.number.isRequired,

  onTimeExpire: PropTypes.func,
};

QuizTimer.defaultProps = {
  onTimeExpire: undefined,
};

export default QuizTimer;
